import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { ModalView } from '../providers/modal.provider';
import useTokenRefresh from '../hooks/useTokenRefresh';

const TokenRefreshModal = () => {
  const { t } = useTranslation();
  const { showRefreshWarning, refreshToken, dismissAndLogout, tokenExpireTime } =
    useTokenRefresh();
  const [countdown, setCountdown] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Calculate countdown to expiration
  useEffect(() => {
    if (!showRefreshWarning || !tokenExpireTime) return;

    const interval = setInterval(() => {
      const now = new Date();
      const expireTime = new Date(tokenExpireTime);
      const timeLeft = Math.max(0, Math.floor((expireTime - now) / 1000));
      setCountdown(timeLeft);

      if (timeLeft === 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [showRefreshWarning, tokenExpireTime]);

  const handleExtendSession = async () => {
    // Prevent multiple simultaneous calls
    if (isRefreshing) return;

    setIsRefreshing(true);
    try {
      const success = await refreshToken();
      if (!success) {
        toast.error(t('errorInGeneratingToken') || 'Error in generating token');
      }
    } catch (error) {
      console.error('Error in token refresh:', error);
      toast.error(t('errorInGeneratingToken') || 'Error in generating token');
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (!showRefreshWarning) return null;

  return (
    <ModalView
      size='lg'
      fader={'show'}
      title={t('sessionExpiringWarning') || 'Session Expiring Soon'}
      onCloseEvent={handleExtendSession}
      footer={
        <>
          <button
            type='button'
            className='btn btn-outline-secondary'
            onClick={() => dismissAndLogout()}
            disabled={isRefreshing}
          >
            {t('logout') || 'Logout'}
          </button>
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleExtendSession}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                ></span>
                {t('extending') || 'Extending...'}
              </>
            ) : (
              t('extendSession') || 'Extend Session'
            )}
          </button>
        </>
      }
    >
      <Box
        className='row align-items-center p-4'
        sx={{ marginRight: '0 !important', marginLeft: '0 !important' }}
      >
        <Box className='col center text-center'>
          <Typography variant='h6' className='mb-3'>
            {t('sessionWillExpireMessage') || 'Your session will expire in:'}
          </Typography>
          <Box className='text-center mb-3'>
            <span className='badge bg-warning fs-4 py-2 px-3'>
              {formatTime(countdown)}{' '}
              {countdown <= 60 ? `${t('seconds') || 'Sec'}` : `${t('minutes') || 'Min'}`}
            </span>
          </Box>
          <Typography variant='body2' color='textSecondary' className='mb-3'>
            {t('clickExtendToContinue') ||
              'Click "Extend Session" to continue working, or you will be automatically logged out.'}
          </Typography>
        </Box>
      </Box>
    </ModalView>
  );
};

export default TokenRefreshModal;
